// useVoiceCommands — wake-word + command listener for cook mode.
//
// State machine (exposed via voice.state):
//   "idle"      — mic on but no wake word heard recently
//   "listening" — wake word detected; 4s window for the command
//   "firing"    — command matched; brief confirmation flash
//
// Audio feedback is split:
//   - Wake heard       → soft single-note chime + light haptic
//   - Command fired    → confirm two-note chime + medium haptic
//
// Two recognition backends:
//   - iOS native: BoodaVoice plugin (custom AVAudioEngine graph with
//                 voice-processing IO + SFSpeechRecognizer + chime
//                 player nodes on the same engine, so chimes don't
//                 fight the audio session).
//   - Web:        Web Speech API (window.SpeechRecognition).
//
// On native iOS, chimes route through the engine's player node and
// don't knock SR offline — the legacy audioFloor claim/release dance
// for chimes is retired. Narration (chadwickAudio + playVoiceline)
// still plays via HTML5 audio outside the engine, so the
// speak-start/speak-end suspension stays in place until phase 3
// routes narration through the engine too.
//
// The matcher (regex over partials, cooldown, consumed-prefix trim)
// is platform-agnostic and unchanged.

import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import BoodaVoice from "./boodaVoice";

const WAKE_WORD = "booda";
// Phonetic alternates the recognizer substitutes when it hears
// "booda" — not a real English word. Add more if console logs show
// other variants.
const WAKE_VARIANTS = [
  "booda", "buddha", "buddy", "buda", "boota", "boyda",
  "bouda", "puda", "boutta",
];
// Each entry has a name (the command we fire through onCommand) and
// a regex that matches the relevant phrase in the trimmed transcript.
// Order matters only for tie-breaking when two patterns match at the
// same character index — we put the timer-state patterns before the
// generic step nav so "pause timer" resolves to TIMER_PAUSE, not
// some accidental NEXT/BACK match (none currently overlap, but as
// the vocabulary grows the rule is "specific before generic").
// Each pattern includes phonetic alternates SFSpeechRecognizer (and
// Web Speech) commonly substitutes for the canonical word. Same
// rationale as WAKE_VARIANTS — short imperative words next to noisy
// kitchen audio get mis-heard, so we accept the close-rhyme version
// as a synonym. Add more here when console logs surface a new miss.
//
//   "next"  → "neck", "necks", "nest"
//   "back"  → "bag",  "buck",  "pack", "bach"
//   "pause" → "paws", "pause"
//   "start" → "stark"
//
// Order matters only for tie-breaking when two patterns match at the
// same character index — timer-state patterns sit before generic
// step nav so "pause timer" can't degrade into a stray nav match.
const COMMAND_PATTERNS = [
  { name: "TIMER_PAUSE", re: /\b(?:pause|paws|stop|hold)(?:\s+the)?\s+timer\b|\btimer\s+(?:pause|paws|stop|hold|off)\b/i },
  { name: "TIMER_RESET", re: /\b(?:restart|reset|redo)(?:\s+the)?\s+timer\b|\btimer\s+(?:restart|reset|redo)\b/i },
  { name: "TIMER_START", re: /\b(?:start|stark|begin|set|go)(?:\s+the)?\s+timer\b|\btimer\s+(?:on|start|stark|begin|go|now)\b|\btimer\s+\d+\b/i },
  { name: "NEXT",   re: /\b(?:next|neck|necks|nest|advance|continue|forward)\b/i },
  { name: "BACK",   re: /\b(?:back|bag|buck|pack|bach|previous|prior|before)\b/i },
  // "Hey Booda repeat" / "...again" / "...read me this step" /
  // "...tell me this step" all re-play Chadwick's audio narration.
  { name: "REPEAT", re: /\b(?:repeat|repete|again|read|tell)\b/i },
];

function escape(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
const WAKE_RE = new RegExp(`\\b(?:${WAKE_VARIANTS.map(escape).join("|")})\\b`, "i");

const COOLDOWN_MS = 2000;
// How long after "Hey Booda" the matcher accepts a follow-up command
// without re-hearing the wake word. On iOS native (BoodaVoice) chimes
// route through the engine and don't knock SR offline, so the window
// could be tighter — but the legacy plugin path (Android) and Web
// Speech path still suffer the suspend cost, so 9s remains a safe
// floor across backends. Real-world: a "Hey Booda <pause> back"
// pattern needs the pause-for-confirmation tolerance regardless.
const WAKE_WINDOW_MS = 9000;
const FIRING_FLASH_MS = 700;
// Gap after audio ends before the recognizer comes back. Long enough
// for iOS to release the audio session cleanly, short enough that
// the user doesn't notice a pause before "LISTENING" returns.
const RESTART_GAP_MS = 600;
// Initial mount grace — short delay before the first engine start so
// the listener handles attach + permissions settle. Was 3s when HTML5
// narration auto-played and needed to fire speak-start first; now
// narration is engine-routed and DEPENDS on the engine being up, so
// the long grace was actively breaking banner-resume narration.
const INITIAL_GRACE_MS = 250;
// Wake-chime defer. With BoodaVoice the chime no longer fights the
// audio session, so this is purely a UX tweak: a continuous "Hey
// Booda <command>" should produce ONE confirmation chime (the action
// chime), not two (wake → action). Hold the wake chime briefly; if a
// command lands first, handleFire cancels the pending wake chime and
// only the action chime plays. If no command lands, the user said
// just the wake word and gets the wake chime as confirmation.
const WAKE_CHIME_DEFER_MS = 500;

function findWakeEnd(text) {
  if (!text) return -1;
  const m = WAKE_RE.exec(text);
  return m ? m.index + m[0].length : -1;
}

function findCommand(text) {
  if (!text) return null;
  let bestName = null;
  let bestIdx = Infinity;
  for (const { name, re } of COMMAND_PATTERNS) {
    const m = re.exec(text);
    if (m && m.index < bestIdx) {
      bestName = name;
      bestIdx = m.index;
    }
  }
  return bestName;
}

function lightHaptic() {
  if (Capacitor.isNativePlatform()) {
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  } else if (typeof navigator !== "undefined" && navigator.vibrate) {
    try { navigator.vibrate(8); } catch {}
  }
}
function mediumHaptic() {
  if (Capacitor.isNativePlatform()) {
    Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
  } else if (typeof navigator !== "undefined" && navigator.vibrate) {
    try { navigator.vibrate(20); } catch {}
  }
}

// Chimes — two paths depending on backend:
//
// iOS native (BoodaVoice): chimes are pre-synthesized PCM buffers on
// the engine's chime AVAudioPlayerNode. Voice-processing IO keeps the
// recognizer from hearing them, so no audio-session juggling, no
// audioFloor claim, no SR pause. Just `BoodaVoice.playChime("wake" |
// "action")` — fires synchronously, plays through the same engine
// that's listening.
//
// Web: pre-rendered 16-bit PCM WAV blobs played via HTML5 <audio>.
// Web Speech tolerates parallel audio playback so this path also
// avoids any session juggling. Blob URLs are built lazily and cached
// at module scope.

function buildWavBlob(tones, sampleRate = 22050) {
  // tones: [{ freq, startMs, durationMs, gain }]
  const totalMs = Math.max(...tones.map((t) => t.startMs + t.durationMs)) + 30;
  const totalSamples = Math.ceil((sampleRate * totalMs) / 1000);
  const buffer = new ArrayBuffer(44 + totalSamples * 2);
  const view = new DataView(buffer);
  const writeStr = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + totalSamples * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, totalSamples * 2, true);
  for (let i = 0; i < totalSamples; i++) {
    const t = (i / sampleRate) * 1000; // ms into clip
    let sample = 0;
    for (const tone of tones) {
      if (t < tone.startMs || t > tone.startMs + tone.durationMs) continue;
      const localT = (t - tone.startMs) / 1000;
      const localDur = tone.durationMs / 1000;
      // Quick attack (12ms linear), exponential decay over the rest.
      const attack = 0.012;
      let env;
      if (localT < attack) env = localT / attack;
      else env = Math.exp(-4 * (localT - attack) / (localDur - attack));
      sample += Math.sin(2 * Math.PI * tone.freq * localT) * tone.gain * env;
    }
    sample = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + i * 2, sample * 0x7fff, true);
  }
  return new Blob([buffer], { type: "audio/wav" });
}

let _wakeUrl = null;
let _actionUrl = null;
function ensureChimeUrls() {
  if (typeof window === "undefined" || typeof URL === "undefined") return;
  if (!_wakeUrl) {
    const blob = buildWavBlob([{ freq: 880, startMs: 0, durationMs: 140, gain: 0.75 }]);
    _wakeUrl = URL.createObjectURL(blob);
  }
  if (!_actionUrl) {
    const blob = buildWavBlob([
      { freq: 523.25, startMs: 0, durationMs: 180, gain: 0.65 },
      { freq: 783.99, startMs: 70, durationMs: 220, gain: 0.65 },
    ]);
    _actionUrl = URL.createObjectURL(blob);
  }
}

// iOS: route through the BoodaVoice engine — same audio pipeline as
// narration. VPIO subtracts the chime from the mic feed hardware-side,
// no feedback, no audio-session-juggling. The earlier HTML5 <audio>
// path silently failed to fire when SR held the audio session.
//
// Web: fall back to the pre-rendered WAV blobs played via HTML5
// <audio> (browsers don't have the engine).
function playActionChime() {
  if (Capacitor.isNativePlatform() && BoodaVoice.supported) {
    BoodaVoice.playChime("action").catch((e) =>
      console.log(`[chime] action engine failed: ${e?.message || e}`)
    );
    return;
  }
  ensureChimeUrls();
  if (!_actionUrl) return;
  const audio = new Audio(_actionUrl);
  audio.preload = "auto";
  audio.play().catch((e) => console.log(`[chime] action <audio> failed: ${e?.message || e}`));
}

function playWakeChime() {
  if (Capacitor.isNativePlatform() && BoodaVoice.supported) {
    BoodaVoice.playChime("wake").catch((e) =>
      console.log(`[chime] wake engine failed: ${e?.message || e}`)
    );
    return;
  }
  ensureChimeUrls();
  if (!_wakeUrl) return;
  const audio = new Audio(_wakeUrl);
  audio.preload = "auto";
  audio.play().catch((e) => console.log(`[chime] wake <audio> failed: ${e?.message || e}`));
}

function getWebRecognizerCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}
function detectSupported() {
  if (typeof window === "undefined") return false;
  // Native: only iOS (BoodaVoice). Android has no native backend
  // post phase-2 swap; the legacy community plugin was removed.
  if (Capacitor.isNativePlatform()) return BoodaVoice.supported;
  return !!getWebRecognizerCtor();
}

export function useVoiceCommands({ enabled, onCommand } = {}) {
  const [lastCommand, setLastCommand] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceState, setVoiceState] = useState("idle"); // idle | listening | firing
  const [error, setError] = useState(null);
  const supported = detectSupported();

  const lastFireRef = useRef(0);
  const consumedPrefixRef = useRef("");
  const wakeUntilRef = useRef(0);
  const wakeChimedAtRef = useRef(0);
  const stateTimerRef = useRef(null);
  // Deferred wake-chime timer — armed by noteWakeOnly, cleared by
  // handleFire when a command lands inside the defer window.
  const pendingWakeChimeRef = useRef(null);
  // No narration guard. Per-step narration on iOS now plays through
  // BoodaVoice.playNarration → narrationPlayer on the engine's audio
  // graph with VPIO enabled, so hardware AEC subtracts the playback
  // from the mic input before SR sees it. The recognizer hears the
  // user clean over Booda's own voice; no need to drop partials.
  // Web fallback (HTML5 <audio>) relies on the browser's getUserMedia
  // AEC. The audioFloor speak-start/-end events still dispatch (music
  // ducker listens), we just don't gate the matcher on them.
  const onCommandRef = useRef(onCommand);
  useEffect(() => { onCommandRef.current = onCommand; }, [onCommand]);

  const enterListening = useCallback(() => {
    setVoiceState("listening");
    if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
    stateTimerRef.current = setTimeout(() => {
      setVoiceState((s) => (s === "listening" ? "idle" : s));
    }, WAKE_WINDOW_MS);
  }, []);

  const enterFiring = useCallback(() => {
    setVoiceState("firing");
    if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
    stateTimerRef.current = setTimeout(() => {
      setVoiceState("idle");
    }, FIRING_FLASH_MS);
  }, []);

  const handleFire = useCallback((command, fullText) => {
    const now = Date.now();
    if (now - lastFireRef.current < COOLDOWN_MS) {
      console.log(`[voice:fire-block] cooldown ${command} (${now - lastFireRef.current}ms < ${COOLDOWN_MS})`);
      return false;
    }
    console.log(`[voice:fire] ${command} text="${fullText}"`);
    // Cancel any pending wake chime — the action chime is about to
    // fire and they'd just talk over each other.
    if (pendingWakeChimeRef.current) {
      clearTimeout(pendingWakeChimeRef.current);
      pendingWakeChimeRef.current = null;
      console.log("[chime] wake → cancelled (command landed in defer window)");
    }
    lastFireRef.current = now;
    consumedPrefixRef.current = fullText || "";
    wakeUntilRef.current = 0;
    // Show the listening pulse first if we never entered it (continuous
    // "Hey Booda <command>" partials skip noteWakeOnly because the
    // command lands in the same partial as the wake — without this,
    // the user goes straight idle → firing and the bloom/ring visual
    // is never seen). enterFiring 100ms later overrides the listening
    // timer and runs its own 700ms flash. Result: the user sees the
    // bloom rush in, then the green flash overlays it.
    enterListening();
    mediumHaptic();
    playActionChime();
    setLastCommand(command);
    try { onCommandRef.current?.(command); }
    catch (e) { console.warn("[voiceCommands] onCommand threw:", e); }
    setTimeout(() => { enterFiring(); }, 100);
    return true;
  }, [enterFiring, enterListening]);

  const noteWakeOnly = useCallback(() => {
    const now = Date.now();
    // Don't double-haptic within a 1.5s window — partials can
    // re-detect the wake variant repeatedly as the buffer grows.
    if (now - wakeChimedAtRef.current < 1500) return;
    wakeChimedAtRef.current = now;
    lightHaptic();
    enterListening();
    // Defer the chime so a continuous "Hey Booda <command>" utterance
    // gets to fire its command and cancel the chime first (handleFire
    // clears pendingWakeChimeRef). If no command lands within the
    // defer window, the chime plays as wake-confirmation. Voice-
    // processing IO is OFF now, so the chime no longer masks the
    // user's follow-up word — the masking issue that originally
    // disabled this was VPIO-induced, not a fundamental problem.
    if (pendingWakeChimeRef.current) clearTimeout(pendingWakeChimeRef.current);
    pendingWakeChimeRef.current = setTimeout(() => {
      pendingWakeChimeRef.current = null;
      playWakeChime();
    }, WAKE_CHIME_DEFER_MS);
  }, [enterListening]);

  const unconsumedSlice = useCallback((text) => {
    const prefix = consumedPrefixRef.current;
    if (!prefix) return text;
    if (text.startsWith(prefix)) return text.slice(prefix.length);
    consumedPrefixRef.current = "";
    return text;
  }, []);

  const evaluatePartial = useCallback((fullText) => {
    if (!fullText) return false;
    const fresh = unconsumedSlice(fullText);
    const now = Date.now();

    const wakeEnd = findWakeEnd(fresh);
    console.log(`[voice:partial] full="${fullText}" fresh="${fresh}" wakeEnd=${wakeEnd} wakeWinOpen=${wakeUntilRef.current > now}`);
    if (wakeEnd >= 0) {
      // Pause narration on wake. Listener (StepNarrationAudio) holds the
      // playhead in place; auto-resume happens after WAKE_WINDOW_MS if
      // no command lands, OR a command-driven step change supersedes
      // the clip with new src + fresh playEngineNarration call.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("voice:wake-paused", {
          detail: { resumeAfterMs: WAKE_WINDOW_MS },
        }));
      }
      const afterWake = fresh.slice(wakeEnd);
      const cmd = findCommand(afterWake);
      console.log(`[voice:wake] afterWake="${afterWake}" cmd=${cmd || "(none)"}`);
      if (cmd) return handleFire(cmd, fullText);
      noteWakeOnly();
      wakeUntilRef.current = now + WAKE_WINDOW_MS;
      return false;
    }

    if (wakeUntilRef.current > now) {
      const cmd = findCommand(fresh);
      console.log(`[voice:cmd-in-window] fresh="${fresh}" cmd=${cmd || "(none)"}`);
      if (cmd) return handleFire(cmd, fullText);
    }
    return false;
  }, [unconsumedSlice, handleFire, noteWakeOnly]);

  useEffect(() => {
    if (!enabled || !supported) {
      setVoiceState("idle");
      return undefined;
    }

    let cancelled = false;

    if (Capacitor.isNativePlatform() && BoodaVoice.supported) {
      // BoodaVoice owns the audio session and auto-rearms the
      // recognition task internally — JS just streams partials in,
      // fires commands, and stops the engine on unmount. The legacy
      // start/stop/restart/pendingStop dance is gone; the engine
      // handles task lifecycle in Swift, and engine-routed chimes
      // don't conflict with SR.
      //
      // Two safety nets remain on the JS side:
      //   1. Heartbeat — if listeningState says we're stopped and
      //      no narration is playing, kick the engine. Cheap insurance
      //      against any path where the engine slips into stopped
      //      without JS noticing.
      //   2. Error listener — if the Swift side gives up on rearm
      //      (after its own retry budget), explicitly stop+restart
      //      from JS so the user isn't stuck in a dead-mic state.
      //
      // Narration (chadwickAudio + playVoiceline) still plays via
      // HTML5 audio outside the engine, so speak-start → stop /
      // speak-end → start stays. Phase 3 routes narration through
      // the engine's narration player and retires this last bit.
      let partialHandle = null;
      let stateHandle = null;
      let errorHandle = null;
      let debugHandle = null;
      let initialStartTimer = null;
      let heartbeatTimer = null;
      let pluginRunning = false;
      // Guards a re-entrant start while one's already in flight. The
      // BoodaVoice native start() is idempotent (guards on isStarted)
      // so a redundant call is harmless, but skipping the round-trip
      // when we know one's pending keeps logs cleaner.
      let starting = false;

      const startEngine = async () => {
        if (cancelled || starting || pluginRunning) return;
        // Phase 3: no floor / suspendedForAudio guards. Engine stays
        // alive through narration — voice-processing IO handles AEC.
        console.log("[voice] BoodaVoice.start ▶");
        starting = true;
        try {
          await BoodaVoice.start({ language: "en-US" });
        } catch (e) {
          console.log(`[voice] BoodaVoice.start failed: ${e?.message || e}`);
          if (!cancelled) setError(e);
        } finally {
          starting = false;
        }
      };

      const stopEngine = async () => {
        if (cancelled) return;
        console.log("[voice] BoodaVoice.stop ■");
        try { await BoodaVoice.stop(); } catch { /* ignore */ }
      };

      // Phase 3: engine STAYS ALIVE during chadwick narration. Narration
      // routes through BoodaVoice.playNarration → narrationPlayer on the
      // same engine, so voice-processing IO subtracts the playback from
      // the mic input and Booda hears the user over Booda. No more
      // speak-start → stopEngine suspension. The floor still fires its
      // events for music ducking (BackgroundMusic listens), but we
      // intentionally don't subscribe.
      const onSpeakStart = () => {
        if (cancelled) return;
        console.log("[voice] speak-start observed (engine stays alive — VPIO handles AEC)");
      };
      const onSpeakEnd = () => {
        if (cancelled) return;
        console.log("[voice] speak-end observed (no-op, engine never stopped)");
      };
      window.addEventListener("chadwick:speak-start", onSpeakStart);
      window.addEventListener("chadwick:speak-end", onSpeakEnd);

      (async () => {
        try {
          const perm = await BoodaVoice.checkPermissions();
          const speechOk = perm?.speechRecognition === "granted";
          const micOk = perm?.microphone === "granted";
          if (!speechOk || !micOk) {
            const req = await BoodaVoice.requestPermissions();
            if (req?.speechRecognition !== "granted" || req?.microphone !== "granted") {
              if (!cancelled) setError(new Error("Mic + speech recognition permissions required"));
              return;
            }
          }
          if (cancelled) return;

          partialHandle = await BoodaVoice.addListener("partialResult", ({ text }) => {
            if (cancelled || !text) return;
            evaluatePartial(text);
          });

          stateHandle = await BoodaVoice.addListener("listeningState", ({ status }) => {
            if (cancelled) return;
            console.log(`[voice] listeningState=${status}`);
            pluginRunning = status === "started";
            setListening(pluginRunning);
          });

          // Engine-side rearm has its own retry budget. If it gives
          // up after that, the engine is dead until JS kicks it.
          // Mirror what the legacy heartbeat did — stop + start with
          // a short gap to clear any stuck recognizer state.
          errorHandle = await BoodaVoice.addListener("error", ({ message }) => {
            if (cancelled) return;
            console.log(`[voice] BoodaVoice error: ${message} — recycling engine`);
            stopEngine().then(() => {
              setTimeout(() => {
                if (cancelled) return;
                startEngine();
              }, RESTART_GAP_MS);
            });
          });

          // Diagnostic stream — Swift forwards isFinal, taskError, and
          // rearm events here so we can see them in Safari Web Inspector
          // instead of just Xcode's console.
          debugHandle = await BoodaVoice.addListener("debug", ({ message }) => {
            if (cancelled || !message) return;
            console.log(`[BoodaVoice:swift] ${message}`);
          });

          // Phase 3: short initial grace just to let the BoodaVoice
          // listeners settle before the first start. Narration playing
          // concurrently is fine — engine + narrationPlayer share the
          // graph and VPIO subtracts the playback from the mic input.
          initialStartTimer = setTimeout(() => {
            initialStartTimer = null;
            if (cancelled) return;
            startEngine();
          }, INITIAL_GRACE_MS);

          // Heartbeat — last-line safety net. Engine should auto-rearm
          // internally; this catches the case where it gives up or a
          // listeningState=stopped event drops. Cheap (~1 syscall) and
          // start() is idempotent on the Swift side.
          heartbeatTimer = setInterval(() => {
            if (cancelled) return;
            if (starting || pluginRunning) return;
            if (initialStartTimer) return;
            console.log("[voice] heartbeat → engine looks stopped, restarting");
            startEngine();
          }, 3000);
        } catch (e) {
          if (!cancelled) setError(e);
        }
      })();

      return () => {
        cancelled = true;
        if (initialStartTimer) clearTimeout(initialStartTimer);
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
        if (pendingWakeChimeRef.current) clearTimeout(pendingWakeChimeRef.current);
        window.removeEventListener("chadwick:speak-start", onSpeakStart);
        window.removeEventListener("chadwick:speak-end", onSpeakEnd);
        partialHandle?.remove?.().catch(() => {});
        stateHandle?.remove?.().catch(() => {});
        errorHandle?.remove?.().catch(() => {});
        debugHandle?.remove?.().catch(() => {});
        BoodaVoice.stop().catch(() => {});
        setListening(false);
        setVoiceState("idle");
      };
    }

    // ── Web path ─────────────────────────────────────────────────────
    const Ctor = getWebRecognizerCtor();
    if (!Ctor) return undefined;

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";

    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const transcript = e.results[i][0]?.transcript;
        if (evaluatePartial(transcript)) break;
      }
    };
    rec.onstart = () => { if (!cancelled) setListening(true); };
    rec.onend = () => {
      if (cancelled) return;
      setListening(false);
      try { rec.start(); } catch {}
    };
    rec.onerror = (ev) => {
      if (ev?.error && !["no-speech", "aborted"].includes(ev.error)) {
        if (!cancelled) setError(ev);
      }
    };

    try { rec.start(); }
    catch (e) { setError(e); }

    return () => {
      cancelled = true;
      if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
      if (pendingWakeChimeRef.current) clearTimeout(pendingWakeChimeRef.current);
      rec.onresult = null;
      rec.onstart = null;
      rec.onend = null;
      rec.onerror = null;
      try { rec.stop(); } catch {}
      setListening(false);
      setVoiceState("idle");
    };
  }, [enabled, supported, evaluatePartial]);

  return {
    supported,
    listening,
    state: voiceState,
    lastCommand,
    error,
    wakeScore: listening ? 1 : 0,
    lastTranscript: null,
    start: () => {},
    stop: () => {},
  };
}

export { WAKE_WORD };
